import { FC, useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { WafflesPaginationProps } from "@app/shared/waffles";
import WafflesPagination from "@app/shared/waffles/WafflesPagination";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome, QesIcons } from "@app/shared/icons/qesIcons";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import { NetworkProfileGapDetailRow } from "@app/compliance-connect/network-submission/interfaces";
import { AccessGapTypeEnum } from "@app/projects/network-assessment/provider-hub/enumerators";

const DEFAULT_PAGE_SIZE = 10;

const FILLABILITY_CLASS: Partial<Record<AccessGapTypeEnum, string>> = {
  [AccessGapTypeEnum.FillableBySingleProvider]: "fillable",
  [AccessGapTypeEnum.PartiallyFillableGap]: "partially-fillable",
  [AccessGapTypeEnum.UnfillableGap]: "unfillable",
};

type SortKey = "serviceArea" | "specialty" | "status" | "countySsa" | "countyClass" | "hasexception";
type SortDir = "asc" | "desc" | "none";

interface GapColumn {
  key: SortKey;
  label: string;
}

const columns: GapColumn[] = [
  { key: "serviceArea",  label: "Service Area" },
  { key: "specialty",    label: "Specialty" },
  { key: "status",       label: "Status" },
  { key: "countySsa",    label: "County SSA" },
  { key: "countyClass",  label: "County Class" },
  { key: "hasexception", label: "Exception" },
];

export const buildServiceAreaHref = (row: NetworkProfileGapDetailRow): string | undefined => {
  if (!row.customProjectId) return undefined;
  let href = `${window.customUIAbsolutePath}/projects/${row.customProjectId}/adequacy`;
  if (row.countySsa)
    href += `/county/${row.countySsa}`;
  if (row.specialtyGroupCode)
    href += `/specialty/${row.specialtyGroupCode}`;
  return href;
};

export const buildExceptionsHref = (row: NetworkProfileGapDetailRow): string | undefined => {
  if (!row.customProjectId) return undefined;
  return `${window.customUIAbsolutePath}/exceptions/view?projectId=${row.customProjectId}&specialtyGroupCode=${row.specialtyGroupCode}`;
};

interface NetworkProfileGapDetailsProps {
  submissionId: number;
}

export const NetworkProfileGapDetails: FC<NetworkProfileGapDetailsProps> = ({ submissionId }) => {
  const service = useMemo(() => new NetworkSubmissionService(), []);

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("none");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const sortColumn = sortDir !== "none" && sortKey ? sortKey : undefined;
  const sortDescending = sortDir !== "none" ? sortDir === "desc" : undefined;

  const { data, isLoading } = useQuery({
    enabled: !!submissionId,
    queryFn: ({ signal }) => service.getGapDetails(
      submissionId,
      { pageIndex, pageSize, sortColumn, sortDescending },
      signal),
    queryKey: ["ns-network-profile-gap-details", submissionId, pageIndex, pageSize, sortColumn, sortDescending],
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.items ?? [];
  const totalRecords = data?.totalCount ?? 0;
  const skip = pageIndex * pageSize;

  const handleSort = useCallback((key: SortKey) => {
    if (key !== sortKey) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortKey(null);
      setSortDir("none");
    }
    setPageIndex(0);
  }, [sortKey, sortDir]);

  const getSortIcon = useCallback((key: SortKey): QesIcons => {
    if (key !== sortKey || sortDir === "none") return QesIcons.ArrowsVertical;
    return sortDir === "asc" ? QesIcons.ArrowNarrowUp : QesIcons.ArrowNarrowDown;
  }, [sortKey, sortDir]);

  const handlePageChange = useCallback((pageNumber: number) => {
    setPageIndex(pageNumber - 1);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageIndex(0);
    setPageSize(newSize);
  }, []);

  const paginationProps = useMemo<WafflesPaginationProps>(() => ({
    changePage: handlePageChange,
    handlePageSizeChange,
    pageSizeOptions: [10, 25, 50],
    recordsPerPage: pageSize,
    showPageSizeSelector: true,
    skip,
    totalRecords,
  }), [handlePageChange, handlePageSizeChange, pageSize, skip, totalRecords]);

  return (
    <>
      {!isLoading &&
        <div className="compliance-connect-network-profile__gap-details">
          <h4 className="compliance-connect-network-profile__section-title">Gap Details</h4>
          <div className="np-gap-details__table-wrapper">
          <table className="np-gap-details__table" data-testid="np-gap-details__table">
            <thead>
              <tr className="np-gap-details__head-row np-gray-border-bottom">
                <th aria-hidden="true" className="np-gap-details__th np-gap-details__th--indicator" />
                {columns.map(col => (
                  <th
                    className="np-gap-details__th"
                    data-testid={`np-gap-details__${col.key}-col`}
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    scope="col"
                  >
                    {col.label}
                    <QesIcon className="np-gap-details__sort-icon" iconName={getSortIcon(col.key)} isDecorative />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const serviceAreaHref = buildServiceAreaHref(row);
                const exceptionsHref = row.hasException ? buildExceptionsHref(row) : undefined;
                return (
                  <tr
                    className={`np-gap-details__row np-gray-border-bottom${FILLABILITY_CLASS[row.fillability] ? ` np-gap-details__row--${FILLABILITY_CLASS[row.fillability]}` : ""}`}
                    data-testid={`np-gap-details__row-${row.countySsa}`}
                    key={`${row.countySsa}-${row.specialtyGroup}`}
                  >
                    <td aria-hidden="true" className="np-gap-details__td np-gap-details__td--indicator" />
                    <td className="np-gap-details__td">
                      {serviceAreaHref ? (
                        <a data-testid="np-gap-details__service-area-link" href={serviceAreaHref} rel="noopener noreferrer" target="_blank">
                          {row.serviceAreaName}
                          {" "}<QesIcon iconName={FontAwesome.ExternalLink} isDecorative />
                        </a>
                      ) : (
                        <span>{row.serviceAreaName}</span>
                      )}
                    </td>
                    <td className="np-gap-details__td">{row.specialtyGroup}</td>
                    <td className="np-gap-details__td">{row.status}</td>
                    <td className="np-gap-details__td">{row.countySsa}</td>
                    <td className="np-gap-details__td">{row.countyClass}</td>
                    <td className="np-gap-details__td">
                      {exceptionsHref
                        ? <a data-testid="np-gap-details__exceptions-link" href={exceptionsHref} rel="noopener noreferrer" target="_blank">{row.hasException ? "Yes" : "No"}</a>
                        : row.hasException ? "Yes" : "No"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <WafflesPagination {...paginationProps} />
        </div>
      }
    </>
  );
};
